import React, { useMemo } from "react";
import Footer from "./common/Footer";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  CssBaseline,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AddCircleOutline,
  CloudOutlined,
  QuestionAnswerOutlined,
  LabelOutlined,
  FilterListOutlined,
  PictureAsPdfOutlined,
  Security,
} from "@mui/icons-material";

const Feature = React.memo(({ icon, title, description }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 4,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: "transform 0.2s ease-out, box-shadow 0.2s ease-out",
        willChange: "transform, box-shadow",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Box
        sx={{
          mb: 3,
          color: theme.palette.primary.main,
          transition: "transform 0.2s ease-out",
          willChange: "transform",
          "&:hover": {
            transform: "scale(1.1)",
          },
        }}
      >
        {React.cloneElement(icon, { fontSize: "large" })}
      </Box>
      <Typography
        variant="h6"
        component="h3"
        gutterBottom
        align="center"
        sx={{ fontWeight: "bold", mb: 2 }}
      >
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        {description}
      </Typography>
    </Box>
  );
});

const LandingPage = React.memo(({ onGetStarted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const features = useMemo(
    () => [
      {
        icon: <AddCircleOutline />,
        title: "Create Custom Projects",
        description:
          "Tailor your security review to specific AWS project needs.",
      },
      {
        icon: <CloudOutlined />,
        title: "Select AWS Services",
        description:
          "Choose the AWS services you're using for a personalized review.",
      },
      {
        icon: <QuestionAnswerOutlined />,
        title: "Generate Relevant Questions",
        description:
          "Get targeted questions on security, performance, and data management.",
      },
      {
        icon: <LabelOutlined />,
        title: "Customize and Label",
        description: "Add custom questions and labels for easy categorization.",
      },
      {
        icon: <FilterListOutlined />,
        title: "Filter and Organize",
        description:
          "Focus on specific aspects of your security review with ease.",
      },
      {
        icon: <PictureAsPdfOutlined />,
        title: "Export and Share",
        description:
          "Export your review to PDF for easy sharing with your team.",
      },
    ],
    [],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      <CssBaseline />
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: 8 }}>
        <Box
          sx={{
            textAlign: "center",
            mb: 12,
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-50%",
              left: "-25%",
              right: "-25%",
              bottom: "-50%",
              background: `radial-gradient(circle, ${theme.palette.primary.main}33 0%, transparent 70%)`,
              transform: "rotate(-12deg)",
            }}
          />
          <Security
            sx={{
              fontSize: 80,
              color: theme.palette.primary.main,
              mb: 4,
              position: "relative",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": {
                  transform: "scale(1)",
                },
                "50%": {
                  transform: "scale(1.05)",
                },
                "100%": {
                  transform: "scale(1)",
                },
              },
            }}
          />
          <Typography
            variant={isMobile ? "h3" : "h2"}
            component="h1"
            gutterBottom
            sx={{
              position: "relative",
              mb: 3,
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            AWS Security Review
          </Typography>

          <Typography
            variant={isMobile ? "body1" : "h6"}
            component="p"
            gutterBottom
            color="text.secondary"
            sx={{ mb: 6, maxWidth: "800px", mx: "auto", position: "relative" }}
          >
            Streamline your AWS security assessment process with our
            comprehensive review tool
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onGetStarted}
            sx={{
              py: 2,
              px: 6,
              fontSize: "1.2rem",
              borderRadius: "50px",
              boxShadow: theme.shadows[10],
              transition: "all 0.2s ease-out",
              willChange: "transform, box-shadow",
              "&:hover": {
                transform: "translateY(-2px) scale(1.05)",
                boxShadow: theme.shadows[20],
              },
              position: "relative",
            }}
          >
            Get Started
          </Button>
        </Box>

        <Grid container spacing={6} justifyContent="center">
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Feature {...feature} />
            </Grid>
          ))}
        </Grid>
      </Container>
      <Footer />
    </Box>
  );
});

export default LandingPage;
